import { toast } from '@apps/web/lib/app_notifications';
import {
    formatDayOfWeek,
    formatTime,
    getDateStringInTimezone,
} from '@apps/web/lib/date_utils';
import {
    organizationSupportsShiftRecurrence,
    withTemplateVariableAliases,
} from '@apps/web/lib/template_variables';
import {
    ApiError,
    type Organization,
    type Outreach,
    type OutreachAcceptedCaregiver,
    type OutreachResponseCounts,
    type Shift,
} from '@libraries/typescript/api_client';

function ordinalSuffix(day: number): string {
    if (day >= 11 && day <= 13) {
        return 'th';
    }
    return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th';
}

/**
 * Format a date as "February 18th" (full month with ordinal day).
 */
function formatFullMonthDate(iso: string, tz: string): string {
    const dt = new Date(iso);
    const month = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        month: 'long',
    }).format(dt);
    const day = Number(
        new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            day: 'numeric',
        }).format(dt),
    );
    return `${month} ${day}${ordinalSuffix(day)}`;
}

/**
 * Mirrors the backend get_relative_shift_date:
 * - today:    "today, Wednesday, February 18th"
 * - tomorrow: "tomorrow, Thursday, February 19th"
 * - other:    "Saturday, February 22nd"
 */
function formatRelativeShiftDate(iso: string, tz: string): string {
    const todayStr = getDateStringInTimezone(new Date(), tz);
    const shiftStr = getDateStringInTimezone(new Date(iso), tz);

    const todayMs = new Date(todayStr + 'T00:00:00').getTime();
    const shiftMs = new Date(shiftStr + 'T00:00:00').getTime();
    const daysDiff = Math.round((shiftMs - todayMs) / 86_400_000);

    const dateStr = formatFullMonthDate(iso, tz);
    const dayOfWeek = formatDayOfWeek(iso, tz);

    if (daysDiff === 0) {
        return `today, ${dayOfWeek}, ${dateStr}`;
    }
    if (daysDiff === 1) {
        return `tomorrow, ${dayOfWeek}, ${dateStr}`;
    }
    return `${dayOfWeek}, ${dateStr}`;
}

const TRAILING_PARENS_RE = /\s*\([^)]*\)\s*$/;
const LEADING_HOUSE_NUMBER_RE = /^\d+[-\d]*\s+/;
const TRAILING_UNIT_RE =
    /[,\s]+(?:#\s*\S+|(?:apt|unit|suite|ste|bldg|fl|floor|rm|room|lot|spc|space)\.?\s*\S*)$/i;
// Keep this equivalent to the profile-linking suffix list used for client names.
const GENERATIONAL_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv']);
// WellSky service-type tags appended to client names ("John Smith Non Skilled").
const TRAILING_SERVICE_TYPE_RE = /\s+(?:non[\s-]?)?skilled$/i;

export function trimPatientNameAnnotations(patientName: string): string {
    return patientName.replace(TRAILING_PARENS_RE, '').trim();
}

export function getClientInitials(
    patientName: string | null | undefined,
): string {
    return getClientInitialsParts(patientName).full;
}

export function getClientInitialsParts(
    patientName: string | null | undefined,
): { full: string; first: string; last: string } {
    const normalizedName = trimPatientNameAnnotations(patientName || '')
        .replace(TRAILING_SERVICE_TYPE_RE, '')
        .trim();
    if (!normalizedName || normalizedName === 'the patient') {
        return { full: '', first: '', last: '' };
    }
    let parts = normalizedName.split(/\s+/);
    while (
        parts.length > 1 &&
        GENERATIONAL_SUFFIXES.has(
            parts[parts.length - 1]!.replace(/\.+$/, '').toLowerCase(),
        )
    ) {
        parts = parts.slice(0, -1);
    }
    if (parts.length === 1) {
        const first = `${parts[0]![0]!.toUpperCase()}.`;
        return { full: first, first, last: '' };
    }
    const first = `${parts[0]![0]!.toUpperCase()}.`;
    const last = `${parts[parts.length - 1]![0]!.toUpperCase()}.`;
    return { full: `${first}${last}`, first, last };
}

export function formatInitialsForSpeech(initials: string): string {
    return initials.replace(/\.\s*/g, '. ').trim();
}

export function extractStreetName(address: string | null | undefined): string {
    if (!address) {
        return '';
    }
    return address
        .trim()
        .replace(LEADING_HOUSE_NUMBER_RE, '')
        .replace(TRAILING_UNIT_RE, '')
        .trim();
}

export function formatFullAddress(
    street: string | null | undefined,
    city: string | null | undefined,
    state: string | null | undefined,
    postalCode: string | null | undefined,
): string {
    const streetParts = (street || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    const existingParts = new Set(
        streetParts.map((value) => value.toLowerCase()),
    );
    const parts = [...streetParts];
    const cityPart = city?.trim() || '';
    const locality = [state, postalCode]
        .map((value) => value?.trim() || '')
        .filter(Boolean)
        .join(' ');
    if (cityPart && !existingParts.has(cityPart.toLowerCase())) {
        parts.push(cityPart);
    }
    if (locality && !existingParts.has(locality.toLowerCase())) {
        parts.push(locality);
    }
    return parts.join(', ');
}

/**
 * Keep caregiver-facing previews on the delivery privacy contract: client
 * initials, city/state locality, and empty street and ZIP values.
 */
export function buildCaregiverSafeClientTemplateValues({
    clientName,
    city,
    state,
    includeName = true,
    includeAddress = true,
}: {
    readonly clientName: string | null | undefined;
    readonly city: string | null | undefined;
    readonly state: string | null | undefined;
    readonly includeName?: boolean;
    readonly includeAddress?: boolean;
}): Record<string, string> {
    const initials = getClientInitialsParts(clientName);
    const cityValue = city || '';
    const stateValue = state || '';
    return {
        client_address: includeAddress
            ? formatFullAddress(null, cityValue, stateValue, null)
            : '',
        client_city: cityValue,
        client_first_initial: includeName ? initials.first : '',
        client_first_name: includeName ? initials.first : '',
        client_initials: includeName ? initials.full : '',
        client_last_initial: includeName ? initials.last : '',
        client_last_name: includeName ? initials.last : '',
        client_state: stateValue,
        client_street_name: '',
        client_zip_code: '',
        location_text: cityValue ? ` in ${cityValue}` : '',
    };
}

function buildFullClientTemplateValues({
    clientName,
    address,
    city,
    state,
    zipCode,
}: {
    readonly clientName: string | null | undefined;
    readonly address: string | null | undefined;
    readonly city: string | null | undefined;
    readonly state: string | null | undefined;
    readonly zipCode: string | null | undefined;
}): Record<string, string> {
    const normalizedName = trimPatientNameAnnotations(clientName || '');
    const initials = getClientInitialsParts(clientName);
    const nameParts = normalizedName.split(' ');
    const cityValue = city || '';
    return {
        client_address: formatFullAddress(address, city, state, zipCode),
        client_city: cityValue,
        client_first_initial: initials.first,
        client_first_name: nameParts[0] || '',
        client_initials: initials.full,
        client_last_initial: initials.last,
        client_last_name: nameParts.slice(1).join(' '),
        client_state: state || '',
        client_street_name: extractStreetName(address || ''),
        client_zip_code: zipCode || '',
        location_text: cityValue ? ` in ${cityValue}` : '',
    };
}

export function getLabelForOutreachType(
    outreach_type: Outreach['outreach_type'],
) {
    switch (outreach_type) {
        case 'recurring_shift':
            return 'Recurring Shift';
        case 'clock_in':
            return 'Clock In Reminder';
        case 'clock_out':
            return 'Clock Out Reminder';
        case 'shift_confirmation':
            return 'Shift Confirmation';
        default:
            return 'Open Shift';
    }
}

export function isClockReminderOutreach(
    outreach_type: Outreach['outreach_type'],
): boolean {
    return outreach_type === 'clock_in' || outreach_type === 'clock_out';
}

export function supportsOutreachAutoAssignFirstResponder(
    outreachType: Outreach['outreach_type'] | undefined,
): boolean {
    return outreachType === 'callout' || outreachType === 'recurring_shift';
}

/**
 * The joined outreach read carries the configured per-shift auto-assign set
 * as a first-class field for both agent generations; the field is absent only
 * on reads that predate it, and an empty set means auto-assign is off.
 */
type OutreachAutoAssignRead = {
    readonly auto_assign_shift_ids?: ReadonlyArray<string>;
};

export function resolveOutreachAutoAssignFirstResponder(
    outreach: OutreachAutoAssignRead | null | undefined,
): boolean {
    return getAutoAssignShiftIds(outreach).length > 0;
}

export function getAutoAssignShiftIds(
    outreach: OutreachAutoAssignRead | null | undefined,
): ReadonlyArray<string> {
    return outreach?.auto_assign_shift_ids ?? [];
}

export const OUTREACH_URGENCY_METADATA_KEY = 'outreach_urgency';

export function getOutreachUrgency(
    outreachMetadata: Record<string, unknown> | null | undefined,
): 'normal' | 'urgent' {
    return outreachMetadata?.[OUTREACH_URGENCY_METADATA_KEY] === 'urgent'
        ? 'urgent'
        : 'normal';
}

export function isUrgentOutreach(
    outreachMetadata: Record<string, unknown> | null | undefined,
): boolean {
    return getOutreachUrgency(outreachMetadata) === 'urgent';
}

export function getScheduledOutreachStartAt(
    outreachMetadata: Record<string, unknown> | null | undefined,
): string | null {
    const value = outreachMetadata?.['scheduled_start_at'];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The timestamp a fill outreach was paused at, or null if not paused.
 */
export function getOutreachPausedAt(
    outreachMetadata: Record<string, unknown> | null | undefined,
): string | null {
    const value = outreachMetadata?.['paused_at'];
    return typeof value === 'string' ? value : null;
}

/**
 * The set of shift ids already filled by a fill outreach. Backend writes
 * `filled_shifts` as a record keyed by shift id; returns an empty set when the
 * key is missing or not a record.
 */
export function getFilledShiftIds(
    outreachMetadata: Record<string, unknown> | null | undefined,
): ReadonlySet<string> {
    const value = outreachMetadata?.['filled_shifts'];
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return new Set();
    }
    return new Set(Object.keys(value));
}

/**
 * The caregiver assigned to a shift when the outreach was created. A callout
 * records the called-out caregiver per shift in `original_caregivers` (a
 * record of shift id → caregiver id, null for shifts that had no caregiver).
 * Returns null when nothing was recorded for the shift.
 */
export function getOriginalCaregiverIdForShift(
    outreachMetadata: Record<string, unknown> | null | undefined,
    shiftId: string,
): string | null {
    const value = outreachMetadata?.['original_caregivers'];
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const caregiverId = Object.entries(value).find(
            ([id]) => id === shiftId,
        )?.[1];
        return typeof caregiverId === 'string' && caregiverId.length > 0
            ? caregiverId
            : null;
    }
    // Rows that predate the map recorded one `original_caregiver_id` for
    // their single shift; the backend read path keeps the same fallback.
    const legacyCaregiverId = outreachMetadata?.['original_caregiver_id'];
    return typeof legacyCaregiverId === 'string' && legacyCaregiverId.length > 0
        ? legacyCaregiverId
        : null;
}

export const OUTREACH_REMOVAL_REASON_METADATA_KEY = 'removal_reason';

const OUTREACH_REMOVAL_REASONS = [
    'cancelled',
    'filled',
    'filled-externally',
    'no_longer_available',
] as const;

export type OutreachRemovalReason = (typeof OUTREACH_REMOVAL_REASONS)[number];

/**
 * The structured archive reason stamped when an outreach is terminally
 * cancelled (the free-text cancellation reason is prose and must not be
 * parsed). Null for rows archived before the stamp existed, for system
 * cancellations that record no reason, and for unrecognized values —
 * readers must treat null as a generic archive.
 */
export function getOutreachRemovalReason(
    outreachMetadata: Record<string, unknown> | null | undefined,
): OutreachRemovalReason | null {
    const value = outreachMetadata?.[OUTREACH_REMOVAL_REASON_METADATA_KEY];
    return OUTREACH_REMOVAL_REASONS.find((reason) => reason === value) ?? null;
}

/**
 * When a terminal outreach was resolved (archived, filled, or expired), or
 * null for rows that predate the stamp — display fallback is `created_at`.
 */
export function getOutreachResolvedAt(
    outreachMetadata: Record<string, unknown> | null | undefined,
): string | null {
    const value = outreachMetadata?.['resolved_at'];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Yes count for one shift of a fill outreach. `accepted_count` counts a
 * caregiver who said yes to any shift in the outreach, so on multi-shift
 * outreaches it overstates each individual shift; per-shift UI must use the
 * `accepted_count_by_shift_id` map instead. Payloads from an API that predates
 * the map fall back to the outreach total rather than hiding known yeses.
 */
export function getAcceptedCountForShift(
    counts: OutreachResponseCounts | null | undefined,
    shiftId: string,
): number {
    if (!counts) {
        return 0;
    }
    const acceptedCountByShiftId = counts.accepted_count_by_shift_id;
    if (acceptedCountByShiftId === undefined) {
        return counts.accepted_count;
    }
    return acceptedCountByShiftId[shiftId] ?? 0;
}

/**
 * Bounded display list of caregivers who said yes to this shift. Empty when
 * nobody accepted, the payload predates the field, or the caregiver records
 * were deleted — and capped server-side — so per-shift counts stay the source
 * of truth: derive any "+N more" overflow from the count, never this list's
 * length.
 */
export function getAcceptedCaregiversForShift(
    counts: OutreachResponseCounts | null | undefined,
    shiftId: string,
): ReadonlyArray<OutreachAcceptedCaregiver> {
    return counts?.accepted_caregivers_by_shift_id?.[shiftId] ?? [];
}

/**
 * The durable audit stamp of when the outreach was scheduled to start, or
 * null if it was never scheduled. Unlike `scheduled_start_at`, this survives
 * both firing and cancel, so the Scheduled ledger can still show the intended
 * time for past and cancelled scheduled outreaches.
 */
export function getWasScheduledOutreachStartAt(
    outreachMetadata: Record<string, unknown> | null | undefined,
): string | null {
    const value = outreachMetadata?.['was_scheduled_start_at'];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The stashed start time of a *paused* scheduled outreach, or null. Pausing
 * moves the live `scheduled_start_at` here (and drops the auto-start stamp) so
 * the timer/IDLE seam stay inert; resuming restores it.
 */
export function getPausedScheduledOutreachStartAt(
    outreachMetadata: Record<string, unknown> | null | undefined,
): string | null {
    const value = outreachMetadata?.['paused_scheduled_start_at'];
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The start time to display for a scheduled-tab row: the live scheduled time,
 * falling back to the stashed time when paused, then to the durable
 * was_scheduled_start_at stamp for rows that have fired or been cancelled.
 * Non-null for any outreach that is or was scheduled.
 */
export function getEffectiveScheduledOutreachStartAt(
    outreachMetadata: Record<string, unknown> | null | undefined,
): string | null {
    return (
        getScheduledOutreachStartAt(outreachMetadata) ??
        getPausedScheduledOutreachStartAt(outreachMetadata) ??
        getWasScheduledOutreachStartAt(outreachMetadata)
    );
}

export type ScheduledOutreachState = 'scheduled' | 'paused';

/**
 * Whether a scheduled outreach is currently running toward its start time or
 * paused. "Paused" is a pure metadata projection (the outreach status stays
 * PENDING): it is paused when `paused_scheduled_start_at` is set.
 */
export function getScheduledOutreachState(
    outreachMetadata: Record<string, unknown> | null | undefined,
): ScheduledOutreachState {
    return getPausedScheduledOutreachStartAt(outreachMetadata) !== null
        ? 'paused'
        : 'scheduled';
}

export type OutreachStep = {
    readonly step_type: 'sms' | 'voice';
    readonly message_template?: string | null;
    readonly prompt_alteration?: string | null;
    readonly multi_shift_prompt_alteration?: string | null;
    readonly delay_in_minutes: number;
    // Callout SMS templates - per-outreach overrides for organization templates
    readonly callout_template?: string | null;
    readonly callout_multi_shift_template?: string | null;
    readonly callout_multi_shift_all_template?: string | null;
    readonly consolidated_shift_template?: string | null;
    readonly multi_shift_item_template?: string | null;
    // Recurring shift SMS templates
    readonly group_template?: string | null;
    readonly per_group_template?: string | null;
    readonly single_client_template?: string | null;
};

// Types for callout SMS preview generation

export type OpenShiftTemplates = {
    readonly single: string; // 'callout' template
    readonly multiHeader: string; // 'callout_multi_shift' template
    readonly multiItem: string; // 'callout_multi_shift_item' template
};

export type RecurringTemplates = {
    readonly clientTemplate: string;
    readonly groupTemplate: string;
    readonly itemTemplate: string;
    readonly singleClientTemplate?: string | null;
    readonly allShiftsTemplate?: string | null;
    readonly consolidatedShiftTemplate?: string | null;
};

export type ShiftGroupMode = 'all' | 'any';

export type ShiftGroupDraft = {
    readonly shiftIds: Array<string>;
    readonly groupMode: ShiftGroupMode;
};

type ShiftGroupReadLike = {
    readonly shift_ids: Array<string>;
    readonly group_mode: string;
};

function getShiftGroupKey(shift: Pick<Shift, 'client_id' | 'patient_name'>) {
    return shift.client_id || (shift.patient_name || '').trim() || 'Unknown';
}

function normalizeShiftGroupMode(
    mode: string | null | undefined,
): ShiftGroupMode | undefined {
    if (mode === 'all' || mode === 'any') {
        return mode;
    }
    return;
}

function mergeShiftGroupModes(
    existingMode: ShiftGroupMode | undefined,
    nextMode: ShiftGroupMode,
): ShiftGroupMode {
    if (existingMode === 'all' || nextMode === 'all') {
        return 'all';
    }
    return 'any';
}

function getShiftGroupFields(group: ShiftGroupDraft | ShiftGroupReadLike): {
    readonly shiftIds: Array<string>;
    readonly groupMode: string;
} {
    if ('shiftIds' in group) {
        return { shiftIds: group.shiftIds, groupMode: group.groupMode };
    }
    return { shiftIds: group.shift_ids, groupMode: group.group_mode };
}

export function resolveShiftGroupModesByKey({
    shifts,
    shiftGroups,
    legacyShiftGroupKeys,
}: {
    readonly shifts: Array<Pick<Shift, 'id' | 'client_id' | 'patient_name'>>;
    readonly shiftGroups?: Array<ShiftGroupDraft | ShiftGroupReadLike> | null;
    readonly legacyShiftGroupKeys?: ReadonlySet<string> | null;
}): Map<string, ShiftGroupMode> {
    const groupModesByKey = new Map<string, ShiftGroupMode>();

    if (shiftGroups && shiftGroups.length > 0) {
        const shiftsById = new Map(shifts.map((shift) => [shift.id, shift]));

        for (const rawGroup of shiftGroups) {
            const { shiftIds, groupMode: rawMode } =
                getShiftGroupFields(rawGroup);
            const groupMode = normalizeShiftGroupMode(rawMode);
            if (!groupMode) {
                continue;
            }

            const overlappingShifts = shiftIds
                .map((shiftId: string) => shiftsById.get(shiftId))
                .filter(
                    (shift): shift is (typeof shifts)[number] =>
                        shift !== undefined,
                );
            if (overlappingShifts.length <= 1) {
                continue;
            }

            const groupKeys = new Set(
                overlappingShifts.map((shift) => getShiftGroupKey(shift)),
            );
            if (groupKeys.size !== 1) {
                continue;
            }

            const groupKey = groupKeys.values().next().value;
            if (groupKey === undefined) {
                continue;
            }
            const mergedMode = mergeShiftGroupModes(
                groupModesByKey.get(groupKey),
                groupMode,
            );
            groupModesByKey.set(groupKey, mergedMode);
        }
    }

    if (groupModesByKey.size > 0 || !legacyShiftGroupKeys) {
        return groupModesByKey;
    }

    for (const key of legacyShiftGroupKeys) {
        groupModesByKey.set(key, 'all');
    }

    return groupModesByKey;
}

export type ShiftPreviewData = {
    readonly shiftId?: string;
    readonly shiftDate: string;
    readonly shiftDayOfWeek?: string;
    readonly shiftDuration?: string;
    readonly frequency?: Shift['frequency'];
    readonly recurrenceInterval?: number | null;
    readonly startTime: string;
    readonly endTime: string;
    readonly rawStartTime?: string;
    readonly rawEndTime?: string;
    readonly patientCity?: string;
    readonly patientState?: string;
    readonly patientZipCode?: string;
    readonly patientId?: string;
    readonly patientName?: string;
    readonly patientAddress?: string;
    readonly carePlanSummary?: string;
};

export type PreviewContext = {
    readonly caregiverFirstName?: string;
    readonly caregiverLastName?: string;
    readonly caregiverName?: string;
    readonly caregiverDistance?: string;
    readonly previousVisits?: string;
    readonly organizationName?: string;
    readonly assistantName?: string | null;
    readonly careCoordinator?: string;
    readonly clientReferenceTerm?: string;
    readonly supportsDetailedRecurrence?: boolean;
    // The organization's configured Phoebe intro, so a preview of a line
    // containing {phoebe_intro} shows what the caller will actually hear.
    // Undefined falls back to the same default the backend builds.
    readonly phoebeIntro?: string | null;
};

type ShiftCareCoordinator = NonNullable<Shift['care_coordinators']>[number];

export const DEFAULT_SMS_GREETING_PREFIX = 'Hey {caregiver_first_name}, ';

const SMS_GREETING_TEMPLATE_PATTERN =
    /^(?<greetingPrefix>.*?\{caregiver_(?:first_name|last_name|name)\}(?:\s*[,:;.!?-]\s*|\s+)?)(?<messageBody>[\s\S]*)$/i;

export type SmsGreetingTemplateParts = {
    readonly greetingPrefix: string;
    readonly messageBody: string;
};

export function splitSmsGreetingTemplate(
    template: string | null | undefined,
): SmsGreetingTemplateParts {
    const templateValue = template ?? '';
    if (templateValue.length === 0) {
        return {
            greetingPrefix: '',
            messageBody: '',
        };
    }

    const matched = templateValue.match(SMS_GREETING_TEMPLATE_PATTERN);
    if (!matched?.groups) {
        return {
            greetingPrefix: '',
            messageBody: templateValue,
        };
    }

    return {
        greetingPrefix: matched.groups['greetingPrefix'] ?? '',
        messageBody: matched.groups['messageBody'] ?? '',
    };
}

export function resolveSmsGreetingPrefix(
    template: string | null | undefined,
): string {
    if (!template || template.trim().length === 0) {
        return DEFAULT_SMS_GREETING_PREFIX;
    }

    return splitSmsGreetingTemplate(template).greetingPrefix;
}

export function prependSmsGreetingPrefix(
    messageBody: string,
    greetingPrefix: string | null | undefined,
): string {
    if (messageBody.trim().length === 0) {
        return '';
    }

    const prefix = greetingPrefix ?? '';
    if (prefix.length === 0) {
        return messageBody;
    }

    return `${prefix}${messageBody}`;
}

/**
 * Replace all `{key}` placeholders in a template string with values from context.
 */
export function substituteTemplateVariables(
    template: string,
    context: Record<string, string>,
): string {
    let result = template;
    for (const [key, value] of Object.entries(context)) {
        result = result.split(`{${key}}`).join(value);
    }
    return result;
}

function normalizeRecurrenceInterval(
    interval: number | null | undefined,
): number {
    return interval !== undefined && interval !== null && interval >= 1
        ? interval
        : 1;
}

export function formatShiftRecurrence({
    frequency,
    interval,
    dayOfWeek,
}: {
    readonly frequency: Shift['frequency'] | undefined;
    readonly interval: number | null | undefined;
    readonly dayOfWeek: string;
}): string {
    const normalizedFrequency = frequency ?? 'weekly';
    const normalizedInterval = normalizeRecurrenceInterval(interval);

    if (normalizedFrequency === 'daily') {
        return normalizedInterval === 1
            ? 'daily'
            : `every ${normalizedInterval} days`;
    }
    if (normalizedFrequency === 'monthly') {
        return normalizedInterval === 1
            ? 'monthly'
            : `every ${normalizedInterval} months`;
    }
    if (normalizedFrequency === 'weekly') {
        return normalizedInterval === 1
            ? `every ${dayOfWeek}`
            : `every ${normalizedInterval} weeks on ${dayOfWeek}`;
    }
    return normalizedFrequency;
}

export function formatShiftFrequency({
    frequency,
    interval,
    supportsDetailedRecurrence,
}: {
    readonly frequency: Shift['frequency'] | undefined;
    readonly interval: number | null | undefined;
    readonly supportsDetailedRecurrence: boolean;
}): string {
    const normalizedFrequency =
        frequency ?? (supportsDetailedRecurrence ? 'weekly' : '');
    if (!supportsDetailedRecurrence) {
        return normalizedFrequency;
    }
    const normalizedInterval = normalizeRecurrenceInterval(interval);
    if (normalizedInterval === 1) {
        return normalizedFrequency;
    }
    const unitByFrequency: Readonly<Record<string, string>> = {
        daily: 'days',
        weekly: 'weeks',
        monthly: 'months',
    };
    const unit = unitByFrequency[normalizedFrequency];
    return unit ? `every ${normalizedInterval} ${unit}` : normalizedFrequency;
}

const ZIPCODE_VARIABLE_KEYS = new Set(['client_zip_code', 'patient_zip_code']);

/**
 * Replace all `{key}` placeholders in a template string with values from context,
 * EXCEPT for zipcode-related variables which are kept as placeholders.
 * This is used for form fields where users can edit resolved values like care_plan_summary,
 * but zipcode variables need to stay as placeholders for proper voice pronunciation.
 */
export function substituteTemplateVariablesExceptZipcode(
    template: string,
    context: Record<string, string>,
): string {
    let result = template;
    for (const [key, value] of Object.entries(context)) {
        if (ZIPCODE_VARIABLE_KEYS.has(key)) {
            continue;
        }
        result = result.split(`{${key}}`).join(value);
    }
    return result;
}

function formatCareCoordinatorName(coordinator: ShiftCareCoordinator): string {
    return `${coordinator.first_name} ${coordinator.last_name}`.trim();
}

export function getCareCoordinatorNameFromShift(shift: Shift): string {
    const names =
        shift.care_coordinators
            ?.filter((c) => c.include_in_variable)
            .map((coordinator) => formatCareCoordinatorName(coordinator))
            .filter((name) => name.length > 0) ?? [];
    return [...new Set(names)].sort().join(' & ');
}

export function getCareCoordinatorNameFromShifts(shifts: Array<Shift>): string {
    const names = shifts
        .map((shift) => getCareCoordinatorNameFromShift(shift))
        .filter((name) => name.length > 0);
    return [...new Set(names)].sort().join(' & ');
}

function computeShiftDuration(startIso: string, endIso: string): string {
    const totalHours =
        (new Date(endIso).getTime() - new Date(startIso).getTime()) / 3_600_000;

    if (totalHours === Math.trunc(totalHours)) {
        const h = Math.trunc(totalHours);
        return `${h} hour${h !== 1 ? 's' : ''}`;
    }

    const formatted = totalHours
        .toFixed(2)
        .replace(/0+$/, '')
        .replace(/\.$/, '');
    return `${formatted} hours`;
}

export function buildTemplateContext(
    selectedShift: Shift,
    organization: Organization | undefined,
    carePlanSummary?: string,
): Record<string, string> {
    const timezone = organization?.timezone || 'UTC';

    const shiftDate = formatRelativeShiftDate(
        selectedShift.shift_start_time,
        timezone,
    );
    const shiftDayOfWeek = formatDayOfWeek(
        selectedShift.shift_start_time,
        timezone,
    );
    const shiftDuration = computeShiftDuration(
        selectedShift.shift_start_time,
        selectedShift.shift_end_time,
    );
    const startTime = formatTime(selectedShift.shift_start_time, timezone);
    const endTime = formatTime(selectedShift.shift_end_time, timezone);
    const shiftFrequency = formatShiftFrequency({
        frequency: selectedShift.frequency,
        interval: selectedShift.recurrence_interval,
        supportsDetailedRecurrence:
            organizationSupportsShiftRecurrence(organization),
    });

    return withTemplateVariableAliases({
        caregiver_first_name: '{caregiver_first_name}',
        caregiver_last_name: '{caregiver_last_name}',
        caregiver_name: '{caregiver_name}',
        care_coordinator:
            getCareCoordinatorNameFromShift(selectedShift) ||
            '{care_coordinator}',
        organization_name: organization?.name || '',
        shift_date: shiftDate,
        shift_day_of_week: shiftDayOfWeek,
        shift_frequency: shiftFrequency,
        shift_recurrence: shiftFrequency,
        shift_duration: shiftDuration,
        shift_start_time: startTime,
        shift_end_time: endTime,
        ...buildCaregiverSafeClientTemplateValues({
            clientName: selectedShift.patient_name,
            city: selectedShift.patient_city,
            state: selectedShift.patient_state,
        }),
        care_plan_summary: carePlanSummary || '',
        caregiver_distance: '{caregiver_distance}',
        previous_visits: '{previous_visits}',
    });
}

function withLegacyGenericClientReference(
    context: Readonly<Record<string, string>>,
    clientReferenceTerm: string | undefined,
): Record<string, string> {
    const normalizedReferenceTerm =
        clientReferenceTerm?.trim().toLowerCase() === 'patient'
            ? 'patient'
            : 'client';
    return withTemplateVariableAliases({
        ...context,
        client_first_name: `a ${normalizedReferenceTerm}`,
        client_last_name: `a ${normalizedReferenceTerm}`,
        patient_first_name: `a ${normalizedReferenceTerm}`,
        patient_last_name: `a ${normalizedReferenceTerm}`,
    });
}

export function buildVoiceTemplateContext(
    selectedShift: Shift,
    organization: Organization | undefined,
): Record<string, string> {
    const baseContext = buildTemplateContext(selectedShift, organization);
    return withLegacyGenericClientReference(
        {
            ...baseContext,
            ...buildFullClientTemplateValues({
                clientName: selectedShift.patient_name,
                address: selectedShift.patient_address,
                city: selectedShift.patient_city,
                state: selectedShift.patient_state,
                zipCode: selectedShift.patient_postal_code,
            }),
            // Dispatch speaks separated initials so the provider reads two
            // letters rather than running them together, so every voice
            // preview has to match. SMS keeps the compact form.
            client_initials: formatInitialsForSpeech(
                baseContext['client_initials'] || '',
            ),
        },
        organization?.general_settings?.client_reference_term,
    );
}

/**
 * Build template variable context from ShiftPreviewData (settings page previews).
 */
function buildTemplateContextFromPreviewData(
    shift: ShiftPreviewData,
    context: PreviewContext,
): Record<string, string> {
    const shiftFrequency = formatShiftFrequency({
        frequency: shift.frequency,
        interval: shift.recurrenceInterval,
        supportsDetailedRecurrence: context.supportsDetailedRecurrence === true,
    });

    return withTemplateVariableAliases({
        caregiver_first_name:
            context.caregiverFirstName || '{caregiver_first_name}',
        caregiver_last_name:
            context.caregiverLastName || '{caregiver_last_name}',
        caregiver_name: context.caregiverName || '{caregiver_name}',
        caregiver_distance: context.caregiverDistance || '{caregiver_distance}',
        previous_visits: context.previousVisits || '{previous_visits}',
        care_coordinator: context.careCoordinator || '{care_coordinator}',
        organization_name: context.organizationName || '',
        shift_date: shift.shiftDate,
        shift_day_of_week: shift.shiftDayOfWeek || '',
        shift_frequency: shiftFrequency,
        shift_recurrence: shiftFrequency,
        shift_duration: shift.shiftDuration || '',
        shift_start_time: shift.startTime,
        shift_end_time: shift.endTime,
        ...buildCaregiverSafeClientTemplateValues({
            clientName: shift.patientName,
            city: shift.patientCity,
            state: shift.patientState,
        }),
        care_plan_summary: shift.carePlanSummary || '',
    });
}

export type ClockReminderPreviewContext = PreviewContext & {
    readonly patientFirstName?: string;
    readonly patientLastName?: string;
    readonly patientName?: string;
    readonly patientCity?: string;
    readonly patientState?: string;
    readonly patientZipCode?: string;
    readonly patientAddress?: string;
    readonly timeAndAttendancePin?: string;
    readonly mentionEvv?: boolean;
};

function buildClockReminderSubstitutions(
    shift: ShiftPreviewData,
    context: ClockReminderPreviewContext,
): Record<string, string> {
    const fallbackPatientName = `${context.patientFirstName || 'Pat'} ${
        context.patientLastName || 'Sample'
    }`.trim();
    const patientName =
        context.patientName || shift.patientName || fallbackPatientName;
    const patientCity = context.patientCity || shift.patientCity;
    const patientState = context.patientState || shift.patientState;
    return withTemplateVariableAliases({
        caregiver_first_name:
            context.caregiverFirstName || '{caregiver_first_name}',
        caregiver_last_name:
            context.caregiverLastName || '{caregiver_last_name}',
        caregiver_name: context.caregiverName || '{caregiver_name}',
        organization_name: context.organizationName || '{organization_name}',
        shift_date: shift.shiftDate,
        shift_start_time: shift.startTime,
        shift_end_time: shift.endTime,
        ...buildCaregiverSafeClientTemplateValues({
            clientName: patientName,
            city: patientCity,
            state: patientState,
        }),
        time_and_attendance_pin: context.timeAndAttendancePin || '1234',
        evv_text: context.mentionEvv ? 'and EVV ' : '',
        ehr: '{ehr}',
    });
}

/**
 * Renders an SMS preview for clock-in / clock-out reminder templates.
 *
 * Substitutes the variables the clock_in_sms / clock_out_sms manifest flows
 * offer (libraries/python/template_variables/registry.py) so admins
 * previewing a template see every placeholder — including
 * {time_and_attendance_pin} — rendered with a sample value, matching
 * what libraries/python/shifts/generate_message.py emits at send time.
 *
 * Deliberately does not substitute organization_name: it is a voice
 * opening-line-only variable (see generateClockVoiceOpeningLinePreview) that
 * the clock SMS flows never offer, so the SMS field preview keeps showing
 * it as an unsupported raw token.
 */
export function generateClockReminderSmsPreview(
    template: string,
    shift: ShiftPreviewData,
    context: ClockReminderPreviewContext,
): string {
    return substituteTemplateVariables(
        template,
        buildClockReminderSubstitutions(shift, context),
    );
}

/**
 * Resolve the intro a preview should substitute for {phoebe_intro}.
 *
 * Mirrors the runtime: the organization's configured intro when it has one,
 * otherwise the same default both dispatch sites build, and either way its
 * own placeholders are filled first. A preview that skipped that second step
 * would show a clean sentence for a configuration that speaks a raw token.
 */
function resolvePreviewPhoebeIntro(context: PreviewContext): string {
    const organizationName = context.organizationName || 'your agency';
    const assistantName = context.assistantName?.trim() || 'Phoebe';
    const intro =
        context.phoebeIntro?.trim() ||
        `it's ${assistantName} with ${organizationName}`;
    // Mirrors renderPhoebeIntro on the backend: both name tokens resolve to
    // the same greeting name, because the intro is one spoken clause.
    const caregiverName = context.caregiverFirstName?.trim() || 'there';
    return substituteTemplateVariables(intro, {
        organization_name: organizationName,
        caregiver_first_name: caregiverName,
        caregiver_name: caregiverName,
    });
}

/**
 * Voice-scoped extension of generateClockReminderSmsPreview: adds
 * organization_name and phoebe_intro,
 * which the clock_reminder_voice_opening_line manifest flow offers but the
 * clock SMS flows do not. Kept out of the SMS-facing preview above so the
 * SMS field preview keeps showing them as unsupported raw tokens.
 */
export function generateClockVoiceOpeningLinePreview(
    template: string,
    shift: ShiftPreviewData,
    context: ClockReminderPreviewContext,
): string {
    return substituteTemplateVariables(template, {
        ...buildClockReminderSubstitutions(shift, context),
        organization_name: context.organizationName || '{organization_name}',
        phoebe_intro: resolvePreviewPhoebeIntro(context),
        shift_start_time: shift.startTime,
        shift_end_time: shift.endTime,
    });
}

/**
 * Extends buildTemplateContextFromPreviewData with variables offered only on
 * the callout/recurring single-shift voice opening-line strip
 * (CALLOUT_SINGLE_SHIFT_OPENING_LINE_VARIABLES /
 * RECURRING_SHIFT_OPENING_LINE_VARIABLES in outreach_templates_card.tsx).
 * These tokens are never offered on any SMS template field, so they are
 * kept out of buildTemplateContextFromPreviewData (shared with the SMS
 * previews) and only added here, for the voice opening-line preview.
 */
function buildVoiceOpeningLineContext(
    shift: ShiftPreviewData,
    context: PreviewContext,
): Record<string, string> {
    const baseContext = buildTemplateContextFromPreviewData(shift, context);
    return withLegacyGenericClientReference(
        {
            ...baseContext,
            ...buildFullClientTemplateValues({
                clientName: shift.patientName,
                address: shift.patientAddress,
                city: shift.patientCity,
                state: shift.patientState,
                zipCode: shift.patientZipCode,
            }),
            client_initials: formatInitialsForSpeech(
                baseContext['client_initials'] || '',
            ),
            shift_start_time: shift.startTime,
            shift_end_time: shift.endTime,
            role_required: 'HHA',
            phoebe_intro: resolvePreviewPhoebeIntro(context),
        },
        context.clientReferenceTerm,
    );
}

/**
 * Renders the callout/recurring single-shift voice opening-line preview. See
 * buildVoiceOpeningLineContext for why this is a distinct function from
 * generateOpenShiftSmsPreview rather than an extra field on PreviewContext.
 */
export function generateVoiceOpeningLinePreview(
    template: string,
    shift: ShiftPreviewData,
    context: PreviewContext,
): string {
    return substituteTemplateVariables(
        template,
        buildVoiceOpeningLineContext(shift, context),
    );
}

/**
 * Generates SMS preview from org templates and shift data.
 * Works for both single shift and multi-shift scenarios.
 */
export function generateOpenShiftSmsPreview(
    templates: OpenShiftTemplates,
    shifts: Array<ShiftPreviewData>,
    context: PreviewContext,
): string {
    if (shifts.length === 1) {
        return substituteSingleShift(templates.single, shifts[0]!, context);
    }
    return substituteMultiShift(
        templates.multiHeader,
        templates.multiItem,
        shifts,
        context,
    );
}

function substituteSingleShift(
    template: string,
    shift: ShiftPreviewData,
    context: PreviewContext,
): string {
    return substituteTemplateVariables(
        template,
        buildTemplateContextFromPreviewData(shift, context),
    );
}

function substituteMultiShift(
    headerTemplate: string,
    itemTemplate: string,
    shifts: Array<ShiftPreviewData>,
    context: PreviewContext,
): string {
    const shiftLines = shifts.map((shift, index) => {
        const itemContext = buildTemplateContextFromPreviewData(shift, context);
        const itemLine = substituteTemplateVariables(
            itemTemplate,
            itemContext,
        ).trimEnd();
        return `${index + 1}. ${itemLine}`;
    });

    // If an item expands to multiple lines (e.g. care plans), add a blank line
    // between shift blocks for readability.
    const shiftListJoiner = shiftLines.some((l) => l.includes('\n'))
        ? '\n\n'
        : '\n';
    const shiftList = shiftLines.join(shiftListJoiner);

    const headerContext = withTemplateVariableAliases({
        caregiver_first_name:
            context.caregiverFirstName || '{caregiver_first_name}',
        caregiver_last_name:
            context.caregiverLastName || '{caregiver_last_name}',
        caregiver_name: context.caregiverName || '{caregiver_name}',
        care_coordinator: context.careCoordinator || '{care_coordinator}',
        organization_name: context.organizationName || '',
        shift_count: String(shifts.length),
        shift_list: shiftList,
    });

    return substituteTemplateVariables(headerTemplate, headerContext);
}

const SAMPLE_CARE_PLAN_SUMMARY = 'Light housekeeping and meal prep.';

/**
 * Returns sample shift data for settings page previews.
 */
export function getSampleShifts(): Array<ShiftPreviewData> {
    return [
        {
            carePlanSummary: SAMPLE_CARE_PLAN_SUMMARY,
            shiftDate: 'January 27th',
            shiftDayOfWeek: 'Monday',
            frequency: 'weekly',
            recurrenceInterval: 1,
            shiftDuration: '4 hours',
            startTime: '9am',
            endTime: '1pm',
            patientCity: 'Boston',
            patientZipCode: '02101',
            patientName: 'Jane Smith',
            patientAddress: '123 Main St, Apt 2B',
        },
        {
            carePlanSummary: SAMPLE_CARE_PLAN_SUMMARY,
            shiftDate: 'January 28th',
            shiftDayOfWeek: 'Tuesday',
            frequency: 'weekly',
            recurrenceInterval: 1,
            shiftDuration: '4 hours',
            startTime: '2pm',
            endTime: '6pm',
            patientCity: 'Cambridge',
            patientZipCode: '02139',
            patientName: 'Robert Davis',
            patientAddress: '456 Oak Ave, Unit 4A',
        },
        {
            carePlanSummary: SAMPLE_CARE_PLAN_SUMMARY,
            shiftDate: 'January 29th',
            shiftDayOfWeek: 'Wednesday',
            frequency: 'weekly',
            recurrenceInterval: 1,
            shiftDuration: '4 hours',
            startTime: '10am',
            endTime: '2pm',
            patientCity: 'Somerville',
            patientZipCode: '02143',
            patientName: 'Mary Johnson',
            patientAddress: '789 Elm St #3',
        },
    ];
}

/**
 * Converts a real Shift object to ShiftPreviewData.
 */
const WEEKDAY_ORDER = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

/**
 * Generates a human-readable recurrence description from shifts.
 * e.g. "Every Monday, Wednesday, and Friday from 9:00 AM to 5:00 PM"
 */
export function generateRecurrenceDescription(
    shifts: Array<Shift>,
    timezone: string,
): string {
    if (shifts.length === 0) {
        return '';
    }

    const days = [
        ...new Set(
            shifts.map((s) => formatDayOfWeek(s.shift_start_time, timezone)),
        ),
    ].sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b));

    // Check if all shifts share the same start/end time
    const timeKeys = new Set(
        shifts.map(
            (s) =>
                `${formatTime(s.shift_start_time, timezone)}-${formatTime(s.shift_end_time, timezone)}`,
        ),
    );

    const dayList =
        days.length <= 2
            ? days.join(' and ')
            : `${days.slice(0, -1).join(', ')}, and ${days[days.length - 1]}`;
    const recurrence = formatShiftRecurrence({
        frequency: shifts[0]?.frequency,
        interval: shifts[0]?.recurrence_interval,
        dayOfWeek: dayList,
    });
    const recurrenceDescription =
        recurrence.charAt(0).toUpperCase() + recurrence.slice(1);

    if (timeKeys.size === 1) {
        const startTime = formatTime(shifts[0]!.shift_start_time, timezone);
        const endTime = formatTime(shifts[0]!.shift_end_time, timezone);
        return `${recurrenceDescription} from ${startTime} to ${endTime}`;
    }

    return recurrenceDescription;
}

export function shiftToPreviewData(
    shift: Shift,
    timezone: string,
): ShiftPreviewData {
    return {
        shiftDate: formatRelativeShiftDate(shift.shift_start_time, timezone),
        shiftDayOfWeek: formatDayOfWeek(shift.shift_start_time, timezone),
        shiftDuration: computeShiftDuration(
            shift.shift_start_time,
            shift.shift_end_time,
        ),
        frequency: shift.frequency,
        recurrenceInterval: shift.recurrence_interval,
        startTime: formatTime(shift.shift_start_time, timezone),
        endTime: formatTime(shift.shift_end_time, timezone),
        rawStartTime: shift.shift_start_time,
        rawEndTime: shift.shift_end_time,
        patientCity: shift.patient_city || '',
        patientState: shift.patient_state || '',
        patientZipCode: shift.patient_postal_code || '',
        patientId: shift.client_id || undefined,
        patientName: shift.patient_name || '',
        patientAddress: shift.patient_address || '',
    };
}

/**
 * Converts a real Shift object to ShiftPreviewData using short date format (without year).
 */
export function shiftToPreviewDataShortDate(
    shift: Shift,
    timezone: string,
): ShiftPreviewData {
    return {
        shiftDate: formatRelativeShiftDate(shift.shift_start_time, timezone),
        shiftDayOfWeek: formatDayOfWeek(shift.shift_start_time, timezone),
        shiftDuration: computeShiftDuration(
            shift.shift_start_time,
            shift.shift_end_time,
        ),
        frequency: shift.frequency,
        recurrenceInterval: shift.recurrence_interval,
        startTime: formatTime(shift.shift_start_time, timezone),
        endTime: formatTime(shift.shift_end_time, timezone),
        rawStartTime: shift.shift_start_time,
        rawEndTime: shift.shift_end_time,
        patientCity: shift.patient_city || '',
        patientState: shift.patient_state || '',
        patientZipCode: shift.patient_postal_code || '',
        patientId: shift.client_id || undefined,
        patientName: shift.patient_name || '',
        patientAddress: shift.patient_address || '',
    };
}

// Default recurring shift SMS templates (matching backend)
const DEFAULT_RECURRING_CLIENT_TEMPLATE =
    'Hey {caregiver_first_name},' +
    ' we have recurring shifts available:' +
    '\n\n{client_groups}\n\n' +
    'Are you available for any of these shifts moving forward?';
const DEFAULT_RECURRING_ALL_SHIFTS_CLIENT_TEMPLATE =
    'Hey {caregiver_first_name},' +
    ' we have recurring shifts available:' +
    '\n\n{client_groups}\n\n' +
    'Are you available to take all of these shifts moving forward?';

const DEFAULT_OPEN_SHIFT_CLIENT_TEMPLATE =
    'Hey {caregiver_first_name},' +
    ' we have open shifts available:' +
    '\n\n{client_groups}\n\n' +
    'Are you available for any of the shifts listed above?' +
    ' If so, let us know which shift(s)!';
const DEFAULT_OPEN_SHIFT_CLIENT_TEMPLATE_WITH_SHIFT_GROUPS =
    DEFAULT_OPEN_SHIFT_CLIENT_TEMPLATE.replace(
        '{client_groups}',
        '{shift_groups}',
    );

export const MULTI_SHIFT_ALL_SHIFTS_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have {shift_count} open shifts available:' +
    '\n\n{shift_list}\n\n' +
    'Are you available to take all of these shifts?';

// Back-to-back shifts in one group collapse into a single caregiver-visible
// shift, so the caregiver is asked about one shift. These carry no
// {shift_count}; mirrors CONSOLIDATED_SHIFT_SMS_TEMPLATE in
// libraries/python/outreaches/default_templates.py.
export const CONSOLIDATED_SHIFT_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have an open shift available:' +
    '\n\n{shift_list}\n\n' +
    'Are you available to take this shift?';

export const RECURRING_CONSOLIDATED_SHIFT_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have a recurring shift available:' +
    '\n\n{shift_list}\n\n' +
    'Are you available to take this shift moving forward?';

const DEFAULT_PREVIEW_CAREGIVER_FIRST_NAME = 'Sarah';
const DEFAULT_PREVIEW_CAREGIVER_LAST_NAME = 'Johnson';
const DEFAULT_PREVIEW_CAREGIVER_NAME = 'Sarah Johnson';
const DEFAULT_PREVIEW_CAREGIVER_DISTANCE = '3.2 miles from you';

export const DEFAULT_SINGLE_CLIENT_GROUPED_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have a {client_reference_term} in ' +
    '{patient_city} {caregiver_distance} who needs care ' +
    '{schedule_summary}.\n\n' +
    '{care_plan_summary}\n' +
    'Are you interested in taking on any of the days available with this ' +
    '{client_reference_term}?';

const DEFAULT_RECURRING_GROUP_TEMPLATE = 'Group {group_number}:\n{shift_list}';

// Mirrors RECURRING_SHIFT_PER_GROUP_TEMPLATE in
// libraries/python/outreaches/default_templates.py: the shipped recurring
// template stores this per-group header explicitly, and dispatch treats it
// as a default when deciding whether the consolidated-shift copy applies.
export const RECURRING_SHIFT_PER_GROUP_SMS_TEMPLATE =
    'Client in {patient_city} {caregiver_distance}:\n{shift_list}';

const DEFAULT_RECURRING_ITEM_TEMPLATE =
    '{shift_day_of_week}s, starting ' +
    '{shift_date}, {shift_start_time}-{shift_end_time}{location_text}\n' +
    '{previous_visits}';

const WELLSKY_DEFAULT_RECURRING_ITEM_TEMPLATE =
    '{shift_frequency}, starting ' +
    '{shift_date}, {shift_start_time}-{shift_end_time}{location_text}\n' +
    '{previous_visits}';

const DEFAULT_OPEN_SHIFT_ITEM_TEMPLATE =
    '{shift_date}, {shift_start_time}-{shift_end_time}{location_text} ' +
    '{caregiver_distance}\n{previous_visits}';

export const DEFAULT_RECURRING_TEMPLATES: RecurringTemplates = {
    clientTemplate: DEFAULT_RECURRING_CLIENT_TEMPLATE,
    groupTemplate: DEFAULT_RECURRING_GROUP_TEMPLATE,
    itemTemplate: DEFAULT_RECURRING_ITEM_TEMPLATE,
    consolidatedShiftTemplate: RECURRING_CONSOLIDATED_SHIFT_SMS_TEMPLATE,
};

export const WELLSKY_DEFAULT_RECURRING_TEMPLATES: RecurringTemplates = {
    clientTemplate: DEFAULT_RECURRING_CLIENT_TEMPLATE,
    groupTemplate: DEFAULT_RECURRING_GROUP_TEMPLATE,
    itemTemplate: WELLSKY_DEFAULT_RECURRING_ITEM_TEMPLATE,
    consolidatedShiftTemplate: RECURRING_CONSOLIDATED_SHIFT_SMS_TEMPLATE,
};

export const DEFAULT_OPEN_SHIFT_TEMPLATES: RecurringTemplates = {
    // The {shift_groups} spelling is the literal default dispatch selects
    // and compares against; {client_groups} renders identically (the
    // variables are aliases) but dispatch would treat it as an override.
    clientTemplate: DEFAULT_OPEN_SHIFT_CLIENT_TEMPLATE_WITH_SHIFT_GROUPS,
    groupTemplate: DEFAULT_RECURRING_GROUP_TEMPLATE,
    itemTemplate: DEFAULT_OPEN_SHIFT_ITEM_TEMPLATE,
    allShiftsTemplate: MULTI_SHIFT_ALL_SHIFTS_SMS_TEMPLATE,
    consolidatedShiftTemplate: CONSOLIDATED_SHIFT_SMS_TEMPLATE,
};

/**
 * Generates grouped multi-shift SMS preview, mirroring backend SMS grouping.
 * Structured shift groups are preferred. Legacy client-key grouping is used
 * only as a fallback for older recurring outreaches.
 */
function joinWithAnd(items: ReadonlyArray<string>): string {
    if (items.length <= 1) {
        return items[0] ?? '';
    }
    if (items.length === 2) {
        return `${items[0]} and ${items[1]}`;
    }
    return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
}

export function buildScheduleSummary(
    shifts: ReadonlyArray<ShiftPreviewData>,
    outreachType?: Outreach['outreach_type'],
): string {
    const windowDays = new Map<string, Array<string>>();
    const windowOrder: Array<string> = [];
    const useRecurringWeekdays = outreachType === 'recurring_shift';

    for (const shift of shifts) {
        const interval = useRecurringWeekdays
            ? normalizeRecurrenceInterval(shift.recurrenceInterval)
            : 1;
        const frequency = useRecurringWeekdays
            ? shift.frequency === 'daily' ||
              shift.frequency === 'monthly' ||
              shift.frequency === 'weekly'
                ? shift.frequency
                : 'weekly'
            : '';
        const key = `${frequency}|${interval}|${shift.startTime}|${shift.endTime}`;
        const dayLabel =
            useRecurringWeekdays && shift.shiftDayOfWeek
                ? shift.shiftDayOfWeek
                : shift.shiftDate;

        if (!windowDays.has(key)) {
            windowDays.set(key, []);
            windowOrder.push(key);
        }
        const days = windowDays.get(key)!;
        if (!days.includes(dayLabel)) {
            days.push(dayLabel);
        }
    }

    const parts: Array<string> = [];
    for (const key of windowOrder) {
        const [frequency, intervalText, startTime, endTime] = key.split('|');
        const days = windowDays.get(key)!;
        const interval = Number(intervalText);
        let scheduleText: string;
        if (useRecurringWeekdays && frequency === 'weekly') {
            scheduleText =
                interval === 1
                    ? `on ${joinWithAnd(days.map((day) => `${day}s`))}`
                    : `every ${interval} weeks on ${joinWithAnd(days)}`;
        } else if (useRecurringWeekdays) {
            scheduleText = formatShiftRecurrence({
                frequency: frequency as Shift['frequency'],
                interval,
                dayOfWeek: days[0] ?? '',
            });
        } else {
            scheduleText = `on ${joinWithAnd(days)}`;
        }
        parts.push(`${scheduleText} from ${startTime} to ${endTime}`);
    }

    return joinWithAnd(parts);
}

function computeEarliestShiftDate(
    shifts: ReadonlyArray<ShiftPreviewData>,
): string {
    if (shifts.length === 0) {
        return '';
    }
    const withRaw = shifts.filter((shift) => shift.rawStartTime);
    if (withRaw.length > 0) {
        return withRaw.reduce((earliest, shift) =>
            shift.rawStartTime! < earliest.rawStartTime! ? shift : earliest,
        ).shiftDate;
    }
    return shifts[0]?.shiftDate ?? '';
}

function pluralizeShifts(count: number): string {
    return count === 1 ? '1 shift' : `${count} shifts`;
}

function getSingleClientKey(shift: ShiftPreviewData): string | null {
    if (shift.patientId) {
        return shift.patientId;
    }
    // Mirrors `shift_client_key` in
    // `libraries/python/outreaches/shift_group_utils.py`: the name fallback is
    // casefolded so differently cased names still group as one client.
    // toLowerCase, not toLocaleLowerCase: Python's casefold() is
    // locale-independent, and a locale-sensitive fold (e.g. Turkish dotless I)
    // could pick a different branch than dispatch.
    const patientName = (shift.patientName || '').trim();
    if (!patientName) {
        return null;
    }
    return trimPatientNameAnnotations(patientName).toLowerCase();
}

/**
 * Collapse one group of shifts into the single shift a caregiver is asked
 * about, or return null when the group does not qualify.
 *
 * Consolidation is intentionally strict and mirrors
 * `resolve_contiguous_shift_window` in
 * `libraries/python/outreaches/shift_group_utils.py`: every shift must belong
 * to the same client and location, have a positive duration, and touch the
 * next one exactly. The preview and the Steps editor both call this so they
 * agree on when the consolidated-shift template applies.
 */
export function resolveContiguousShiftPreview(
    groupedShifts: ReadonlyArray<ShiftPreviewData>,
): ShiftPreviewData | null {
    if (
        groupedShifts.length <= 1 ||
        groupedShifts.some((shift) => !shift.rawStartTime || !shift.rawEndTime)
    ) {
        return null;
    }

    const clientKeys = new Set(groupedShifts.map(getSingleClientKey));
    if (clientKeys.size !== 1 || clientKeys.has(null)) {
        return null;
    }

    const normalizeLocation = (shift: ShiftPreviewData) =>
        [
            shift.patientAddress || '',
            shift.patientCity || '',
            shift.patientZipCode || '',
        ]
            .map((value) => value.trim().toLowerCase())
            .join('|');
    if (new Set(groupedShifts.map(normalizeLocation)).size !== 1) {
        return null;
    }

    const orderedShifts = [...groupedShifts].sort(
        (left, right) =>
            new Date(left.rawStartTime!).getTime() -
            new Date(right.rawStartTime!).getTime(),
    );
    if (
        orderedShifts.some(
            (shift) =>
                new Date(shift.rawEndTime!).getTime() <=
                new Date(shift.rawStartTime!).getTime(),
        )
    ) {
        return null;
    }
    const isContiguous = orderedShifts
        .slice(0, -1)
        .every(
            (shift, index) =>
                new Date(shift.rawEndTime!).getTime() ===
                new Date(orderedShifts[index + 1]!.rawStartTime!).getTime(),
        );
    if (!isContiguous) {
        return null;
    }

    const firstShift = orderedShifts[0]!;
    const lastShift = orderedShifts[orderedShifts.length - 1]!;
    return {
        ...firstShift,
        endTime: lastShift.endTime,
        rawEndTime: lastShift.rawEndTime,
        shiftDuration: computeShiftDuration(
            firstShift.rawStartTime!,
            lastShift.rawEndTime!,
        ),
    };
}

export function generateGroupedMultiShiftSmsPreview(
    templates: RecurringTemplates,
    shifts: Array<ShiftPreviewData>,
    context: PreviewContext,
    shiftGroups?: ReadonlyMap<string, ShiftGroupMode>,
    structuredShiftGroups?: ReadonlyArray<
        ShiftGroupDraft | ShiftGroupReadLike
    > | null,
    outreachType?: Outreach['outreach_type'],
): string {
    const clientReferenceTerm = context.clientReferenceTerm || 'client';

    let globalIndex = 1;

    const isSingleClientShiftSet = (): boolean => {
        const clientKeys = new Set(shifts.map(getSingleClientKey));
        return clientKeys.size === 1 && !clientKeys.has(null);
    };

    const renderShiftLine = (shift: ShiftPreviewData): string => {
        const itemContext = buildTemplateContextFromPreviewData(shift, context);
        const line = substituteTemplateVariables(
            templates.itemTemplate,
            itemContext,
        ).trimEnd();
        const numberedLine = `${globalIndex}. ${line}`;
        globalIndex += 1;
        return numberedLine;
    };

    const renderShiftLines = (groupShifts: Array<ShiftPreviewData>) =>
        groupShifts.map((shift) => renderShiftLine(shift));

    const partitionContiguousShiftPreviewRuns = (
        groupedShifts: Array<ShiftPreviewData>,
    ): Array<Array<ShiftPreviewData>> => {
        const orderedShifts = [...groupedShifts].sort((left, right) => {
            if (!left.rawStartTime || !right.rawStartTime) {
                return 0;
            }
            return (
                new Date(left.rawStartTime).getTime() -
                new Date(right.rawStartTime).getTime()
            );
        });
        const normalizeLocation = (shift: ShiftPreviewData) =>
            [
                shift.patientAddress || '',
                shift.patientCity || '',
                shift.patientZipCode || '',
            ]
                .map((value) => value.trim().toLowerCase())
                .join('|');
        const runs: Array<Array<ShiftPreviewData>> = [];
        for (const shift of orderedShifts) {
            const currentRun = runs[runs.length - 1];
            const current = currentRun?.[currentRun.length - 1];
            const canExtend =
                current &&
                current.rawStartTime &&
                current.rawEndTime &&
                shift.rawStartTime &&
                shift.rawEndTime &&
                getSingleClientKey(current) !== null &&
                getSingleClientKey(current) === getSingleClientKey(shift) &&
                normalizeLocation(current) === normalizeLocation(shift) &&
                new Date(current.rawEndTime).getTime() >
                    new Date(current.rawStartTime).getTime() &&
                new Date(shift.rawEndTime).getTime() >
                    new Date(shift.rawStartTime).getTime() &&
                new Date(current.rawEndTime).getTime() ===
                    new Date(shift.rawStartTime).getTime();
            if (canExtend && currentRun) {
                currentRun.push(shift);
            } else {
                runs.push([shift]);
            }
        }
        return runs;
    };

    const renderDisplayShiftLines = (
        groupedShifts: Array<ShiftPreviewData>,
    ): {
        readonly lines: Array<string>;
        readonly consolidatedShift: ShiftPreviewData | null;
    } => {
        const displayRuns = partitionContiguousShiftPreviewRuns(groupedShifts);
        const consolidatedRuns = displayRuns.map((run) =>
            resolveContiguousShiftPreview(run),
        );
        return {
            lines: displayRuns.flatMap((run, index) => {
                const consolidatedRun = consolidatedRuns[index];
                return consolidatedRun
                    ? [renderShiftLine(consolidatedRun)]
                    : renderShiftLines(run);
            }),
            consolidatedShift:
                displayRuns.length === 1 ? (consolidatedRuns[0] ?? null) : null,
        };
    };

    const renderSingleClientShiftList = () => {
        const shiftLines = shifts.map((shift, index) => {
            const itemContext = buildTemplateContextFromPreviewData(
                shift,
                context,
            );
            const line = substituteTemplateVariables(
                templates.itemTemplate,
                itemContext,
            ).trimEnd();
            return `${index + 1}. ${line}`;
        });
        return shiftLines.join(
            shiftLines.some((line) => line.includes('\n')) ? '\n\n' : '\n',
        );
    };

    const joinShiftLines = (shiftLines: Array<string>) =>
        shiftLines.join(
            shiftLines.some((line) => line.includes('\n')) ? '\n\n' : '\n',
        );

    let groupCounter = 0;

    const renderGroupBlock = ({
        shifts: groupedShifts,
        groupMode,
    }: {
        readonly shifts: Array<ShiftPreviewData>;
        readonly groupMode?: ShiftGroupMode;
    }): {
        readonly groupBlock: string;
        readonly shiftList: string;
        readonly consolidatedShift: ShiftPreviewData | null;
    } => {
        groupCounter++;
        const firstShift = groupedShifts[0]!;
        const { lines: shiftLines, consolidatedShift } =
            renderDisplayShiftLines(groupedShifts);
        const shiftList = joinShiftLines(shiftLines);
        const groupHeaderContext = withTemplateVariableAliases({
            group_number: String(groupCounter),
            caregiver_distance:
                context.caregiverDistance ?? DEFAULT_PREVIEW_CAREGIVER_DISTANCE,
            previous_visits: context.previousVisits ?? '{previous_visits}',
            ...buildCaregiverSafeClientTemplateValues({
                clientName: firstShift.patientName,
                city: firstShift.patientCity,
                state: firstShift.patientState,
            }),
            shift_count: pluralizeShifts(groupedShifts.length),
            shift_list: shiftList,
            client_reference_term: clientReferenceTerm,
        });
        let groupBlock = consolidatedShift
            ? shiftList
            : substituteTemplateVariables(
                  templates.groupTemplate,
                  groupHeaderContext,
              );

        if (
            groupMode === 'all' &&
            groupedShifts.length > 1 &&
            !consolidatedShift
        ) {
            const newlineIdx = groupBlock.indexOf('\n');
            const tag = ' (all shifts must be accepted together)';
            if (newlineIdx !== -1) {
                groupBlock =
                    groupBlock.slice(0, newlineIdx) +
                    tag +
                    groupBlock.slice(newlineIdx);
            } else {
                groupBlock += tag;
            }
        }

        return { groupBlock, shiftList, consolidatedShift };
    };

    const renderSingleClientTemplate = (
        shiftGroupsText: string,
        groupMode?: ShiftGroupMode,
        consolidatedShift?: ShiftPreviewData | null,
        consolidatedShiftList?: string | null,
    ): string | null => {
        if (!templates.singleClientTemplate || !isSingleClientShiftSet()) {
            return null;
        }
        const firstShift = shifts[0];
        if (!firstShift) {
            return null;
        }
        const patientCity = firstShift.patientCity || '';
        const groupAcceptanceRequirement =
            groupMode === 'all' && shifts.length > 1 && !consolidatedShift
                ? 'All shifts must be accepted together.\n\n'
                : '';
        const displayShifts = consolidatedShift ? [consolidatedShift] : shifts;
        return substituteTemplateVariables(
            templates.singleClientTemplate,
            withTemplateVariableAliases({
                caregiver_first_name:
                    context.caregiverFirstName ??
                    DEFAULT_PREVIEW_CAREGIVER_FIRST_NAME,
                caregiver_last_name:
                    context.caregiverLastName ??
                    DEFAULT_PREVIEW_CAREGIVER_LAST_NAME,
                caregiver_name:
                    context.caregiverName ?? DEFAULT_PREVIEW_CAREGIVER_NAME,
                organization_name: context.organizationName || '',
                client_reference_term: clientReferenceTerm,
                ...buildCaregiverSafeClientTemplateValues({
                    clientName: firstShift.patientName,
                    city: firstShift.patientCity,
                    state: firstShift.patientState,
                }),
                client_city: patientCity || 'unknown location',
                schedule_summary: buildScheduleSummary(
                    displayShifts,
                    outreachType,
                ),
                start_date: computeEarliestShiftDate(shifts),
                shift_count: pluralizeShifts(displayShifts.length),
                shift_count_number: String(displayShifts.length),
                shift_list:
                    consolidatedShiftList || renderSingleClientShiftList(),
                client_groups: shiftGroupsText,
                shift_groups: shiftGroupsText,
                care_coordinator:
                    context.careCoordinator || '{care_coordinator}',
                // Mirrors dispatch: the first shift with a care plan wins,
                // since every shift here belongs to the same client.
                care_plan_summary:
                    shifts.find((shift) => shift.carePlanSummary)
                        ?.carePlanSummary ?? '',
                previous_visits: context.previousVisits ?? '{previous_visits}',
                caregiver_distance:
                    context.caregiverDistance ??
                    DEFAULT_PREVIEW_CAREGIVER_DISTANCE,
                group_acceptance_requirement: groupAcceptanceRequirement,
            }),
        );
    };

    if (structuredShiftGroups && structuredShiftGroups.length > 0) {
        const groupedBlocks: Array<string> = [];
        const seenShiftIds = new Set<string>();
        let singleGroupMode: ShiftGroupMode | undefined;
        let singleGroupShiftList: string | null = null;
        let singleGroupConsolidatedShift: ShiftPreviewData | null = null;

        for (const rawGroup of structuredShiftGroups) {
            const { shiftIds, groupMode: rawGroupMode } =
                getShiftGroupFields(rawGroup);
            const groupMode = normalizeShiftGroupMode(rawGroupMode);
            const groupShiftIds = new Set(shiftIds);
            const groupedShifts = shifts.filter((shift) => {
                if (!shift.shiftId || seenShiftIds.has(shift.shiftId)) {
                    return false;
                }
                return groupShiftIds.has(shift.shiftId);
            });
            if (groupedShifts.length <= 1) {
                continue;
            }

            const renderedGroup = renderGroupBlock({
                shifts: groupedShifts,
                groupMode,
            });
            groupedBlocks.push(renderedGroup.groupBlock);
            if (groupMode) {
                singleGroupMode = mergeShiftGroupModes(
                    singleGroupMode,
                    groupMode,
                );
            }
            if (groupedBlocks.length === 1) {
                singleGroupShiftList = renderedGroup.shiftList;
                singleGroupConsolidatedShift = renderedGroup.consolidatedShift;
            }
            for (const shift of groupedShifts) {
                if (shift.shiftId) {
                    seenShiftIds.add(shift.shiftId);
                }
            }
        }

        const otherShiftLines = renderShiftLines(
            shifts.filter(
                (shift) => !shift.shiftId || !seenShiftIds.has(shift.shiftId),
            ),
        );
        if (otherShiftLines.length > 0) {
            const otherShiftListJoiner = otherShiftLines.some((line) =>
                line.includes('\n'),
            )
                ? '\n\n'
                : '\n';
            const otherBlock = otherShiftLines.join(otherShiftListJoiner);
            groupedBlocks.push(
                groupedBlocks.length > 0
                    ? `Other shifts:\n${otherBlock}`
                    : otherBlock,
            );
        }

        const shift_groups = groupedBlocks.join('\n\n');
        const consolidatedAllShifts =
            groupedBlocks.length === 1 &&
            otherShiftLines.length === 0 &&
            seenShiftIds.size === shifts.length
                ? singleGroupConsolidatedShift
                : null;
        const singleClientText = renderSingleClientTemplate(
            shift_groups,
            singleGroupMode,
            consolidatedAllShifts,
            consolidatedAllShifts ? singleGroupShiftList : null,
        );
        if (singleClientText !== null) {
            return singleClientText;
        }

        // Dispatch compares against its literal defaults only: the
        // {shift_groups} open-shift wrapper and the recurring wrapper. The
        // {client_groups} open-shift spelling renders identically but counts
        // as an override there, so it must not qualify here either.
        if (
            (templates.clientTemplate ===
                DEFAULT_OPEN_SHIFT_CLIENT_TEMPLATE_WITH_SHIFT_GROUPS ||
                (consolidatedAllShifts !== null &&
                    templates.clientTemplate ===
                        DEFAULT_RECURRING_CLIENT_TEMPLATE)) &&
            (templates.groupTemplate === DEFAULT_RECURRING_GROUP_TEMPLATE ||
                templates.groupTemplate ===
                    RECURRING_SHIFT_PER_GROUP_SMS_TEMPLATE) &&
            groupedBlocks.length === 1 &&
            otherShiftLines.length === 0 &&
            seenShiftIds.size === shifts.length &&
            singleGroupShiftList
        ) {
            if (consolidatedAllShifts) {
                return substituteTemplateVariables(
                    templates.consolidatedShiftTemplate ||
                        (outreachType === 'recurring_shift'
                            ? RECURRING_CONSOLIDATED_SHIFT_SMS_TEMPLATE
                            : CONSOLIDATED_SHIFT_SMS_TEMPLATE),
                    withTemplateVariableAliases({
                        caregiver_first_name:
                            context.caregiverFirstName ||
                            '{caregiver_first_name}',
                        caregiver_last_name:
                            context.caregiverLastName ||
                            '{caregiver_last_name}',
                        caregiver_name:
                            context.caregiverName || '{caregiver_name}',
                        organization_name: context.organizationName || '',
                        care_coordinator:
                            context.careCoordinator || '{care_coordinator}',
                        care_plan_summary: '',
                        shift_list: singleGroupShiftList,
                    }),
                );
            }
            const caregiverFirstName =
                context.caregiverFirstName || '{caregiver_first_name}';
            if (singleGroupMode === 'all') {
                return substituteTemplateVariables(
                    templates.allShiftsTemplate ||
                        MULTI_SHIFT_ALL_SHIFTS_SMS_TEMPLATE,
                    withTemplateVariableAliases({
                        caregiver_first_name: caregiverFirstName,
                        shift_count: String(shifts.length),
                        shift_list: singleGroupShiftList,
                    }),
                );
            }
            return (
                `Hey ${caregiverFirstName}, we have ${shifts.length} open ` +
                `shifts available:\n\n${singleGroupShiftList}\n\n` +
                'Are you available for any of these shifts? If so, let us know which shift(s)!'
            );
        }

        const headerContext = withTemplateVariableAliases({
            caregiver_first_name:
                context.caregiverFirstName || '{caregiver_first_name}',
            caregiver_last_name:
                context.caregiverLastName || '{caregiver_last_name}',
            caregiver_name: context.caregiverName || '{caregiver_name}',
            organization_name: context.organizationName || '',
            client_groups: shift_groups,
            shift_groups,
            shift_count: String(globalIndex - 1),
            shift_list: shift_groups,
            care_coordinator: context.careCoordinator || '{care_coordinator}',
            client_reference_term: clientReferenceTerm,
            care_plan_summary: '',
        });
        const isCompleteAllGroup =
            singleGroupMode === 'all' &&
            groupedBlocks.length === 1 &&
            otherShiftLines.length === 0 &&
            seenShiftIds.size === shifts.length;
        let clientTemplate = templates.clientTemplate;
        if (
            isCompleteAllGroup &&
            clientTemplate === DEFAULT_RECURRING_CLIENT_TEMPLATE
        ) {
            clientTemplate = DEFAULT_RECURRING_ALL_SHIFTS_CLIENT_TEMPLATE;
        }
        return substituteTemplateVariables(clientTemplate, headerContext);
    }

    // Group shifts by client ID (fall back to patient name)
    const grouped = new Map<string, Array<ShiftPreviewData>>();
    for (const shift of shifts) {
        const key = shift.patientId || shift.patientName || 'Unknown';
        const group = grouped.get(key) ?? [];
        group.push(shift);
        grouped.set(key, group);
    }

    const shiftGroupBlocks: Array<string> = [];
    const otherShiftLines: Array<string> = [];
    for (const [groupKey, groupShifts] of grouped) {
        const isGroupedClient = shiftGroups?.has(groupKey) ?? false;
        if (!isGroupedClient) {
            otherShiftLines.push(...renderShiftLines(groupShifts));
            continue;
        }
        shiftGroupBlocks.push(
            renderGroupBlock({
                shifts: groupShifts,
                groupMode: shiftGroups?.get(groupKey),
            }).groupBlock,
        );
    }

    if (otherShiftLines.length > 0) {
        const otherShiftListJoiner = otherShiftLines.some((line) =>
            line.includes('\n'),
        )
            ? '\n\n'
            : '\n';
        const otherBlock = otherShiftLines.join(otherShiftListJoiner);
        shiftGroupBlocks.push(
            shiftGroupBlocks.length > 0
                ? `Other shifts:\n${otherBlock}`
                : otherBlock,
        );
    }

    const shift_groups = shiftGroupBlocks.join('\n\n');
    const singleClientText = renderSingleClientTemplate(shift_groups);
    if (singleClientText !== null) {
        return singleClientText;
    }

    const headerContext = withTemplateVariableAliases({
        caregiver_first_name:
            context.caregiverFirstName || '{caregiver_first_name}',
        caregiver_last_name:
            context.caregiverLastName || '{caregiver_last_name}',
        caregiver_name: context.caregiverName || '{caregiver_name}',
        organization_name: context.organizationName || '',
        client_groups: shift_groups,
        shift_groups,
        shift_count: String(globalIndex - 1),
        shift_list: shift_groups,
        care_coordinator: context.careCoordinator || '{care_coordinator}',
        client_reference_term: clientReferenceTerm,
        care_plan_summary: '',
    });
    return substituteTemplateVariables(templates.clientTemplate, headerContext);
}

export type ConflictingShiftInfo = {
    readonly shift_id: string;
    readonly patient_name: string;
    readonly shift_start_time: string;
    readonly shift_end_time: string;
};

export type ConflictingOutreachInfo = {
    readonly outreach_id: string;
    readonly outreach_type: string;
    readonly status: string;
};

type OutreachConflictDetail = {
    readonly message: string;
    readonly outreach_ids: ReadonlyArray<string>;
    readonly conflicting_outreaches?: ReadonlyArray<ConflictingOutreachInfo>;
    readonly conflicting_shifts: ReadonlyArray<ConflictingShiftInfo>;
};

export type OutreachConflictError = {
    readonly message: string;
    readonly shifts: ReadonlyArray<ConflictingShiftInfo>;
    readonly outreaches: ReadonlyArray<ConflictingOutreachInfo>;
};

export function hasSelectedOutreachConflictShift(
    conflict: OutreachConflictError,
    selectedShiftIds: ReadonlyArray<string>,
    fallbackConflictShiftIds: ReadonlyArray<string> = [],
): boolean {
    const selectedShiftIdSet = new Set(selectedShiftIds);
    const conflictShiftIds =
        conflict.shifts.length > 0
            ? conflict.shifts.map((shift) => shift.shift_id)
            : fallbackConflictShiftIds;
    return conflictShiftIds.some((shiftId) => selectedShiftIdSet.has(shiftId));
}

/**
 * Parse the 409 the create endpoints return when a selected shift is
 * cancelled, yielding the offending shift ids.
 *
 * Distinct from `parseOutreachConflictError`: that conflict names a blocking
 * outreach the coordinator can open, whereas this one means the composer's
 * shift data is stale and must be refetched before the selection can be
 * fixed. Returns `null` for any other 409.
 */
export function parseCancelledShiftSelectionError(
    error: unknown,
): { readonly message: string; readonly shiftIds: Array<string> } | null {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return null;
    }

    const detail = (
        error.body as {
            detail?: {
                readonly message?: unknown;
                readonly cancelled_shift_ids?: unknown;
            };
        }
    )?.detail;
    if (!detail || typeof detail !== 'object') {
        return null;
    }

    const { message, cancelled_shift_ids: shiftIds } = detail;
    if (typeof message !== 'string' || !Array.isArray(shiftIds)) {
        return null;
    }

    return {
        message,
        shiftIds: shiftIds.filter((id): id is string => typeof id === 'string'),
    };
}

/**
 * Parse a 409 outreach conflict error and return structured conflict data.
 */
export function parseOutreachConflictError(
    error: unknown,
): OutreachConflictError | null {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return null;
    }

    const body = error.body as { detail?: OutreachConflictDetail | string };
    const detail = body?.detail;

    if (!detail || typeof detail === 'string') {
        return typeof detail === 'string'
            ? { message: detail, shifts: [], outreaches: [] }
            : null;
    }

    return {
        message: detail.message,
        shifts: detail.conflicting_shifts ?? [],
        outreaches: detail.conflicting_outreaches ?? [],
    };
}

/**
 * Surface an outreach-shift conflict as a toast with a shortcut to the blocking
 * outreach. The blocker may be a type the coordinator cannot see in their
 * current list (e.g. a reminder), so the deep-link lets them jump straight to
 * it instead of hunting for an outreach that appears to not exist.
 */
export function showOutreachConflictToast(
    conflict: OutreachConflictError,
    onViewOutreach: (outreachId: string) => void,
): void {
    const blocker = conflict.outreaches[0];
    toast.error(conflict.message, {
        persistNotification: true,
        notificationType: 'outreach_action',
        ...(blocker
            ? {
                  notificationResource: {
                      type: 'outreach',
                      id: blocker.outreach_id,
                  },
                  notificationNavigationTarget: {
                      kind: 'outreach' as const,
                      outreachId: blocker.outreach_id,
                  },
                  action: {
                      label: 'View outreach',
                      onClick: () => onViewOutreach(blocker.outreach_id),
                  },
              }
            : {}),
    });
}
